import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  verticalPackService,
  type VerticalPack,
  type VerticalPackApplyResult,
} from '../services/verticalPackService';

const OnboardingVerticalPack: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [packs, setPacks] = useState<VerticalPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [applyingSlug, setApplyingSlug] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<VerticalPackApplyResult | null>(null);
  const [error, setError] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await verticalPackService.listVerticalPacks();
        if (!cancelled) {
          setPacks(data);
        }
      } catch {
        if (!cancelled) {
          setError('Unable to load vertical packs. You can continue with generic defaults.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSkip = () => {
    navigate('/onboarding/lead-preferences');
  };

  const handleApply = async (slug: string) => {
    if (!isAdmin) {
      handleSkip();
      return;
    }
    setApplyingSlug(slug);
    setError('');
    try {
      const result = await verticalPackService.applyVerticalPack(slug);
      setApplyResult(result);
      const created = result.email_templates.created;
      showToast(
        created > 0
          ? `Seeded ${created} template${created === 1 ? '' : 's'} and your scoring profile.`
          : 'Vertical pack applied. Settings remain editable.',
        'success'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply vertical pack.');
    } finally {
      setApplyingSlug(null);
    }
  };

  const handleContinue = () => {
    navigate('/onboarding/lead-preferences');
  };

  return (
    <div className="min-h-screen bg-app py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-fg">Choose your industry</h1>
          <p className="text-sm text-fg-muted mt-2">
            Start with a proven profile for your vertical — scoring weights, outreach templates, and chatbot
            defaults. Everything stays editable in Settings.
          </p>
        </div>

        {loading && <p className="text-sm text-fg-muted">Loading vertical packs...</p>}

        {error && (
          <div className="rounded-lg border border-red-300/50 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-800 dark:text-red-100">
            {error}
          </div>
        )}

        {!loading && packs.length === 0 && !error && (
          <p className="text-sm text-fg-muted">No vertical packs are available yet.</p>
        )}

        <div className="grid gap-4">
          {packs.map((pack) => (
            <div key={pack.slug} className="rounded-xl border border-border bg-surface p-6 space-y-3">
              <div>
                <h2 className="text-lg font-semibold text-fg">{pack.name}</h2>
                <p className="text-sm text-fg-muted mt-1">{pack.description}</p>
              </div>
              <p className="text-xs text-fg-muted">
                Includes lead scoring profile, email templates, and chatbot scaffold (v{pack.version}).
              </p>
              {isAdmin ? (
                <button
                  type="button"
                  onClick={() => handleApply(pack.slug)}
                  disabled={applyingSlug === pack.slug}
                  className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:opacity-90 disabled:opacity-60"
                >
                  {applyingSlug === pack.slug ? 'Applying...' : `Apply ${pack.name} pack`}
                </button>
              ) : (
                <p className="text-sm text-fg-muted">
                  Only admins can apply a vertical pack. Continue to set up lead preferences manually.
                </p>
              )}
            </div>
          ))}
        </div>

        {applyResult && (
          <div className="rounded-xl border border-emerald-300/50 bg-emerald-50 dark:bg-emerald-950/30 p-5 space-y-2">
            <h3 className="font-medium text-emerald-900 dark:text-emerald-100">Pack applied</h3>
            <p className="text-sm text-emerald-800 dark:text-emerald-200">{applyResult.message}</p>
            <p className="text-sm text-emerald-800/90 dark:text-emerald-200/90">
              Your lead preferences will highlight industry-relevant options — all editable in Settings.
            </p>
            <ul className="text-xs text-emerald-800/90 dark:text-emerald-200/90 list-disc pl-5 space-y-1">
              <li>
                Email templates: {applyResult.email_templates.created} created,{' '}
                {applyResult.email_templates.skipped} skipped
              </li>
              <li>
                Settings updated: {applyResult.lead_suggestion_settings.updated.join(', ') || 'none'}
              </li>
            </ul>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={handleSkip}
            className="text-sm text-fg-muted hover:text-fg"
          >
            Skip — start with generic defaults
          </button>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-fg-muted hover:text-fg">
              Go to dashboard
            </Link>
            <button
              type="button"
              onClick={handleContinue}
              className="text-sm font-medium text-primary hover:underline"
            >
              Continue to lead preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingVerticalPack;
